import classNames from "classnames";
import React, { useEffect } from "react";
import ReactDOM from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import AssistedSearch from "web/react/components/assisted-search/assisted-search";
import AutoComplete from "web/react/components/auto-complete/auto-complete";
import { AutoCompleteContext } from "web/react/components/auto-complete/auto-complete.context";
import { SearchButton } from "web/react/components/search-button/search-button";
import SVGIcon from "web/react/components/svg-icon/svg-icon";
import {
    closeAppHeaderSearch,
    closeAssistedSearch,
    openAssistedSearch,
} from "web/redux/ducks/app-header-search";
import { addLystMatte, removeLystMatte } from "web/redux/ducks/layout";
import { hideMainNavigation } from "web/redux/ducks/main-navigation";
import { ReduxStoreState } from "web/redux/store";
import analytics from "web/script/analytics/analytics";
import { gettext } from "web/script/modules/django-i18n";
import strings from "web/script/utils/strings";
import { AppHeaderBarSearchInputSerializer } from "web/types/serializers";
import styles from "./header-bar-search.css";

const EVENT_CATEGORY = "nav_search";

export interface HeaderBarSearchProps extends AppHeaderBarSearchInputSerializer {
    className?: string;
    isMobile?: boolean;
}

function extractHeaderSearchState(state: ReduxStoreState): boolean {
    return state.appHeaderSearchReducer.open;
}

function extractSelectedGenderState(state: ReduxStoreState): string {
    return strings.capitalize(state.mainNavigationReducer.selectedGender);
}

function extractAssistedSearchOpenState(state: ReduxStoreState): boolean {
    return state.appHeaderSearchReducer.assistedSearchOpen;
}

function HeaderBarSearchBar({
    className,
    action_url,
    gender,
    query: initialQuery,
    isMobile,
}: HeaderBarSearchProps): React.ReactElement {
    const {
        query,
        updateAutocomplete,
        searchInputRef,
        onKeyDown,
        onSubmit,
        onInput,
        isVisible: autoCompleteIsVisible,
    } = React.useContext(AutoCompleteContext);
    const stateGender = useSelector(extractSelectedGenderState);
    const showMobileAppHeaderSearch = useSelector(extractHeaderSearchState);
    const selectedGender = stateGender || gender;
    const [searchInputFocused, setSearchInputFocused] = React.useState<boolean>(false);
    const [initialInteracted, setInitialInteracted] = React.useState<boolean>(false);
    const [loaded, setLoaded] = React.useState<boolean>(false);
    const dispatch = useDispatch();
    const isAssistedSearchOpen = useSelector(extractAssistedSearchOpenState);

    if (searchInputFocused || isAssistedSearchOpen) {
        dispatch(addLystMatte());
    } else {
        dispatch(removeLystMatte());
    }

    function maybeOpenAssistedSearch(forceClose = false): void {
        if (autoCompleteIsVisible || forceClose) {
            dispatch(closeAssistedSearch());
        } else {
            dispatch(openAssistedSearch());
        }
    }

    useEffect(() => {
        if (initialInteracted) {
            maybeOpenAssistedSearch(autoCompleteIsVisible);
        }
    }, [autoCompleteIsVisible]);

    function close(): void {
        maybeOpenAssistedSearch(true);

        dispatch(closeAppHeaderSearch());
    }

    /* initialize the search value on initial load; query doesn't change */
    React.useEffect(() => {
        if (initialQuery) {
            updateAutocomplete({ query: initialQuery });
        }
        setLoaded(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialQuery]);

    React.useEffect(() => {
        updateAutocomplete({
            gender: selectedGender,
        });
    }, [updateAutocomplete, selectedGender]);

    useEffect(() => {
        function handleClickOutside(event: MouseEvent): void {
            if (
                showMobileAppHeaderSearch &&
                !document
                    .getElementById("app-header-search-bar-mobile")
                    ?.contains?.(event.target as Node)
            ) {
                searchInputRef?.current?.blur();
                close();
            }
        }

        document.addEventListener("mousedown", handleClickOutside);

        return (): void => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [searchInputRef, showMobileAppHeaderSearch, dispatch]);

    function onBlurSearchInput(): void {
        setSearchInputFocused(false);
    }

    function onFocusSearchInput(): void {
        setSearchInputFocused(true);

        if (!initialInteracted) {
            setInitialInteracted(true);
        }

        analytics.event(EVENT_CATEGORY, "search_input_focused");
        dispatch(hideMainNavigation());
        maybeOpenAssistedSearch();
    }

    function onClickCancelButton(): void {
        close();
        analytics.event("header", "clicked", "burger_menu_search_button_close");
    }

    return (
        <span className={classNames(styles.searchWrapper, className)}>
            <form
                className={classNames(styles.search, {
                    [styles.searchFocusedBorder]: searchInputFocused,
                })}
                action={action_url}
                onSubmit={onSubmit}
            >
                <span className={styles.searchIcon}>
                    <SVGIcon name="search" />
                </span>

                <input
                    type="text"
                    className="hidden"
                    name="term"
                    defaultValue={loaded ? query : initialQuery}
                />
                <input type="text" className="hidden" name="gender" defaultValue={selectedGender} />

                <div className={styles.inputContainer}>
                    <button type="submit" className={styles.submitButton}>
                        <SVGIcon name="search" />
                    </button>

                    {/* set autoComplete="off" autoCorrect="off" to disable QuickType on iOS */}
                    <input
                        type="search"
                        className={classNames(styles.input, {
                            [styles.lowercasePlaceholder]: !!stateGender,
                        })}
                        autoComplete="off"
                        autoCorrect="off"
                        name="q"
                        placeholder={
                            !isMobile
                                ? selectedGender === "Men"
                                    ? gettext("header.search.placeholder_for_men")
                                    : gettext("header.search.placeholder_for_women")
                                : undefined
                        }
                        onBlur={onBlurSearchInput}
                        onFocus={onFocusSearchInput}
                        onInput={onInput}
                        onChange={onInput}
                        onKeyDown={onKeyDown}
                        ref={searchInputRef}
                        value={loaded ? query : initialQuery}
                        autoFocus={isMobile}
                    />
                    <button
                        type="button"
                        className={classNames(styles.arrowButton, {
                            hidden: initialInteracted ? (loaded ? !query : !initialQuery) : true,
                        })}
                        onClick={onSubmit}
                    >
                        <SVGIcon name="arrow-right" />
                    </button>
                </div>

                {/* Native keyboard `search` button wouldn't work w/o this w/o displaying a [type=submit] element (can't have display: none) */}
                <input className={styles.submitInput} type="submit" />

                <div
                    className={classNames(styles.autocompleteWrapper, {
                        hidden: !searchInputFocused,
                    })}
                >
                    <AutoComplete />
                </div>
            </form>
            <AssistedSearch defaultGender={gender} />
            <button className={styles.cancelButton} type="button" onClick={onClickCancelButton}>
                <SVGIcon className={styles.cancelIcon} name="cross-thin" />
            </button>
        </span>
    );
}

function HeaderBarSearch(props: HeaderBarSearchProps): React.ReactElement {
    const showMobileAppHeaderSearch = useSelector(extractHeaderSearchState);

    return (
        <>
            <span className="hidden-desktop">
                <SearchButton />
            </span>
            <HeaderBarSearchBar {...props} className="hidden-tablet" />
            {showMobileAppHeaderSearch &&
                ReactDOM.createPortal(
                    <HeaderBarSearchBar {...props} className="hidden-desktop" isMobile />,
                    document.getElementById("app-header-search-bar-mobile")
                )}
        </>
    );
}

export default HeaderBarSearch;
